import React, { useState, useEffect } from 'react';
import { X, Download, Mail, Eye, Trash2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { Event } from '../types';

interface Props {
  event: Event;
  onClose: () => void;
  onUpdate: () => void;
}

export default function ShareModal({ event, onClose, onUpdate }: Props) {
  const [error, setError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [copyCustomSuccess, setCopyCustomSuccess] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBackground, setUploadingBackground] = useState(false);
  const [uploadingTimingLogo, setUploadingTimingLogo] = useState(false);
  const [customSlug, setCustomSlug] = useState<string>(event.custom_url_slug || '');
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [slugError, setSlugError] = useState<string | null>(null);
  const [savingSlug, setSavingSlug] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [disableDownload, setDisableDownload] = useState(event.disable_results_download || false);
  const [savingDownloadSetting, setSavingDownloadSetting] = useState(false);
const [listModeEnabled, setListModeEnabled] = useState(event.list_mode_enabled || false);
const [listModeExactBibSearch, setListModeExactBibSearch] = useState(event.list_mode_exact_bib_search || false);
const [listModeHideFilters, setListModeHideFilters] = useState(event.list_mode_hide_filters || false);
const [listModeDisableResultModal, setListModeDisableResultModal] = useState(event.list_mode_disable_result_modal || false);
const [listModeAutoSelectSearch, setListModeAutoSelectSearch] = useState(event.list_mode_auto_select_search || false);
const [savingListMode, setSavingListMode] = useState(false);

  const getShareUrl = (eventId: string) => {
    return `${window.location.origin}/results/${eventId}`;
  };

  const getCustomShareUrl = (slug: string) => {
    return `${window.location.origin}/results/${slug}`;
  };

  const copyToClipboard = async (text: string, isCustom = false) => {
    try {
      await navigator.clipboard.writeText(text);
      if (isCustom) {
        setCopyCustomSuccess('Copied!');
        setTimeout(() => setCopyCustomSuccess(null), 2000);
      } else {
        setCopySuccess('Copied!');
        setTimeout(() => setCopySuccess(null), 2000);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const validateSlug = (slug: string): boolean => {
    if (!slug) return true; // Empty is valid (removes custom slug)
    
    // Check length
    if (slug.length < 3 || slug.length > 50) {
      setSlugError('Custom URL must be between 3 and 50 characters');
      return false;
    }
    
    // Check format (alphanumeric and hyphens only)
    if (!/^[a-zA-Z0-9-]+$/.test(slug)) {
      setSlugError('Custom URL can only contain letters, numbers, and hyphens');
      return false;
    }
    
    setSlugError(null);
    return true;
  };

  const handleSaveCustomSlug = async () => {
    if (!validateSlug(customSlug)) return;
    
    setError(null);
    setSavingSlug(true);
    
    try {
      console.log("Saving custom slug:", customSlug ? customSlug : null);
      const { error: updateError } = await supabase
        .from('events')
        .update({
          custom_url_slug: customSlug.trim() || null // Convert empty string to null
        })
        .eq('id', event.id);

      if (updateError) throw updateError;
      
      await onUpdate();
      setCopyCustomSuccess(null);
    } catch (err: any) {
      if (err.code === '23505') { // Unique constraint violation
        setSlugError('This custom URL is already taken. Please choose another.');
      } else {
        setError(err.message);
      }
    } finally {
      setSavingSlug(false);
    }
  };

  const handleEventLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError('Logo must be smaller than 2MB');
      return;
    }

    setError(null);
    setUploadingLogo(true);

    try {
      if (event.shared_results_logo?.url) {
        const oldLogoPath = event.shared_results_logo.url.split('/').pop();
        if (oldLogoPath) {
          await supabase.storage
            .from('shared-logos')
            .remove([oldLogoPath]);
        }
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${event.id}-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('shared-logos')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('shared-logos')
        .getPublicUrl(fileName);

      const { error: updateError } = await supabase
        .from('events')
        .update({
          shared_results_logo: {
            url: publicUrl,
            height: 40
          }
        })
        .eq('id', event.id);

      if (updateError) throw updateError;
      
      await onUpdate();
      setPreviewKey(prev => prev + 1);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveEventLogo = async () => {
    if (!event.shared_results_logo?.url) return;

    setError(null);
    setUploadingLogo(true);

    try {
      const logoPath = event.shared_results_logo.url.split('/').pop();
      if (logoPath) {
        await supabase.storage
          .from('shared-logos')
          .remove([logoPath]);
      }

      const { error: updateError } = await supabase
        .from('events')
        .update({
          shared_results_logo: null
        })
        .eq('id', event.id);

      if (updateError) throw updateError;

      await onUpdate();
      setPreviewKey(prev => prev + 1);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleUpdateEventLogoHeight = async (height: number) => {
    if (!event.shared_results_logo) return;

    try {
      const { error } = await supabase
        .from('events')
        .update({
          shared_results_logo: {
            ...event.shared_results_logo,
            height
          }
        })
        .eq('id', event.id);

      if (error) throw error;
      await onUpdate();
      setPreviewKey(prev => prev + 1);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleTimingLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError('Logo must be smaller than 2MB');
      return;
    }

    setError(null);
    setUploadingTimingLogo(true);

    try {
      if (event.timing_company_logo?.url) {
        const oldLogoPath = event.timing_company_logo.url.split('/').pop();
        if (oldLogoPath) {
          await supabase.storage
            .from('shared-logos')
            .remove([oldLogoPath]);
        }
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `timing-${event.id}-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('shared-logos')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('shared-logos')
        .getPublicUrl(fileName);

      const { error: updateError } = await supabase
        .from('events')
        .update({
          timing_company_logo: {
            url: publicUrl,
            name: event.timing_company_logo?.name || ''
          }
        })
        .eq('id', event.id);

      if (updateError) throw updateError;
      
      await onUpdate();
      setPreviewKey(prev => prev + 1);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploadingTimingLogo(false);
    }
  };

  const handleRemoveTimingLogo = async () => {
    if (!event.timing_company_logo?.url) return;

    setError(null);
    setUploadingTimingLogo(true);

    try {
      const logoPath = event.timing_company_logo.url.split('/').pop();
      if (logoPath) {
        await supabase.storage
          .from('shared-logos')
          .remove([logoPath]);
      }

      const { error: updateError } = await supabase
        .from('events')
        .update({
          timing_company_logo: null
        })
        .eq('id', event.id);

      if (updateError) throw updateError;

      await onUpdate();
      setPreviewKey(prev => prev + 1);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploadingTimingLogo(false);
    }
  };

  const handleBackgroundUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Background image must be smaller than 5MB');
      return;
    }

    setError(null);
    setUploadingBackground(true);

    try {
      if (event.shared_results_background?.url) {
        const oldBgPath = event.shared_results_background.url.split('/').pop();
        if (oldBgPath) {
          await supabase.storage
            .from('shared-backgrounds')
            .remove([oldBgPath]);
        }
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${event.id}-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('shared-backgrounds')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('shared-backgrounds')
        .getPublicUrl(fileName);

      const { error: updateError } = await supabase
        .from('events')
        .update({
          shared_results_background: {
            url: publicUrl,
            opacity: 0.15
          }
        })
        .eq('id', event.id);

      if (updateError) throw updateError;
      
      await onUpdate();
      setPreviewKey(prev => prev + 1);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploadingBackground(false);
    }
  };

  const handleRemoveBackground = async () => {
    if (!event.shared_results_background?.url) return;

    setError(null);
    setUploadingBackground(true);

    try {
      const bgPath = event.shared_results_background.url.split('/').pop();
      if (bgPath) {
        await supabase.storage
          .from('shared-backgrounds')
          .remove([bgPath]);
      }

      const { error: updateError } = await supabase
        .from('events')
        .update({
          shared_results_background: null
        })
        .eq('id', event.id);

      if (updateError) throw updateError;

      await onUpdate();
      setPreviewKey(prev => prev + 1);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploadingBackground(false);
    }
  };

  const handleUpdateBackgroundOpacity = async (opacity: number) => {
    if (!event.shared_results_background) return;

    try {
      const { error } = await supabase
        .from('events')
        .update({
          shared_results_background: {
            ...event.shared_results_background,
            opacity
          }
        })
        .eq('id', event.id);

      if (error) throw error;
      await onUpdate();
      setPreviewKey(prev => prev + 1);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleToggleDownload = async () => {
    setError(null);
    setSavingDownloadSetting(true);
    
    try {
      const newValue = !disableDownload;
      const { error: updateError } = await supabase
        .from('events')
        .update({
          disable_results_download: newValue
        })
        .eq('id', event.id);

      if (updateError) throw updateError;
      
      setDisableDownload(newValue);
      await onUpdate();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSavingDownloadSetting(false);
    }
  };
const handleToggleListMode = async () => {
  setSavingListMode(true);
  try {
    const newValue = !listModeEnabled;
    const { error: updateError } = await supabase
      .from('events')
      .update({ list_mode_enabled: newValue })
      .eq('id', event.id);

    if (updateError) throw updateError;

    setListModeEnabled(newValue);
    await onUpdate();
  } catch (err: any) {
    setError(err.message);
  } finally {
    setSavingListMode(false);
  }
};

const handleToggleListModeSetting = async (setting: 'exact_bib' | 'hide_filters' | 'disable_modal' | 'auto_select_search') => {
  setSavingListMode(true);
  try {
    let updateData: any = {};
    let newValue: boolean;

        if (setting === 'exact_bib') {
      newValue = !listModeExactBibSearch;
      updateData.list_mode_exact_bib_search = newValue;
      setListModeExactBibSearch(newValue);
    } else if (setting === 'hide_filters') {
      newValue = !listModeHideFilters;
      updateData.list_mode_hide_filters = newValue;
      setListModeHideFilters(newValue);
    } else if (setting === 'disable_modal') {
      newValue = !listModeDisableResultModal;
      updateData.list_mode_disable_result_modal = newValue;
      setListModeDisableResultModal(newValue);
    } else {
      newValue = !listModeAutoSelectSearch;
      updateData.list_mode_auto_select_search = newValue;
      setListModeAutoSelectSearch(newValue);
    }

    const { error: updateError } = await supabase
      .from('events')
      .update(updateData)
      .eq('id', event.id);

    if (updateError) throw updateError;

    await onUpdate();
  } catch (err: any) {
    setError(err.message);
  } finally {
    setSavingListMode(false);
  }
};

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-[calc(100vh-2rem)] flex items-center justify-center w-full">
        <div className="bg-white/95 backdrop-blur-md rounded-3xl shadow-2xl w-full max-w-4xl my-4 border border-white/50">
          <div className="flex justify-between items-center p-6 border-b border-gray-200/50">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Share Event Results</h2>
              <p className="text-gray-600 mt-1">Configure how your results page appears to participants</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="p-6 space-y-8 max-h-[calc(100vh-16rem)] overflow-y-auto">
            {error && (
              <div className="bg-red-50 text-red-600 p-4 rounded-lg flex items-center gap-2 border border-red-200">
                <X className="w-5 h-5" />
                {error}
              </div>
            )}

            {/* Share URL Section */}
            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Share URL</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Results Page URL
                </label>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="text"
                    value={getShareUrl(event.id)}
                    readOnly
                    className="flex-1 px-3 py-2 bg-white rounded-md border border-gray-300 focus:ring-gray-500 focus:border-gray-500 text-sm font-mono"
                  />
                  <button
                    onClick={() => copyToClipboard(getShareUrl(event.id))}
                    className={`px-4 py-2 ${
                      copySuccess ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-900 hover:bg-gray-800'
                    } text-white rounded-md text-sm min-w-[100px] whitespace-nowrap transition-colors`}
                  >
                    {copySuccess || 'Copy'}
                  </button>
                </div>
                <p className="mt-2 text-sm text-gray-600">
                  Share this link to allow anyone to view the results for this event.
                </p>
              </div>
            </section>

            {/* Custom URL Section */}
            <section className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">Custom URL</h3>
              
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                {event.custom_url_slug ? (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <label className="text-sm font-medium text-gray-700">
                        Custom URL
                      </label>
                      <button
                        onClick={() => setShowRemoveConfirm(true)}
                        className="text-sm text-red-600 hover:text-red-700 font-medium"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <input
                        type="text"
                        value={getCustomShareUrl(event.custom_url_slug)}
                        readOnly
                        className="flex-1 px-3 py-2 bg-white rounded-md border border-gray-300 focus:ring-gray-500 focus:border-gray-500 text-sm font-mono"
                      />
                      <button
                        onClick={() => copyToClipboard(getCustomShareUrl(event.custom_url_slug), true)}
                        className={`px-4 py-2 ${
                          copyCustomSuccess ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-900 hover:bg-gray-800'
                        } text-white rounded-md text-sm min-w-[100px] whitespace-nowrap transition-colors`}
                      >
                        {copyCustomSuccess || 'Copy'}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <label className="block text-sm font-medium text-gray-700 mb-4">
                      Create a custom, memorable URL for sharing your results
                    </label>
                    <div className="flex items-center mb-2">
                      <input
                        type="text"
                        value={customSlug}
                        onChange={(e) => {
                          setCustomSlug(e.target.value);
                          validateSlug(e.target.value);
                        }}
                        placeholder="my-event-name"
                        className={`flex-1 px-3 py-2 bg-white rounded-md border ${
                          slugError ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-gray-500 focus:border-gray-500'
                        } text-sm`}
                      />
                    </div>
                    
                    {customSlug && !slugError && (
                      <div className="mb-4 text-sm text-gray-600 break-all">
                        <span className="font-medium">Preview: </span>{getCustomShareUrl(customSlug)}
                      </div>
                    )}
                    
                    {slugError && (
                      <div className="mb-4 text-sm text-red-500">
                        {slugError}
                      </div>
                    )}
                    
                    <button
                      onClick={handleSaveCustomSlug}
                      disabled={savingSlug || !!slugError || !customSlug}
                      className="px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-md text-sm font-medium transition-colors disabled:opacity-50"
                    >
                      {savingSlug ? 'Saving...' : 'Save Custom URL'}
                    </button>
                  </>
                )}
              </div>
            </section>

            {/* Visual Customization Section */}
                    {/* List Mode Settings Section */}
        <section>
          <h3 className="text-lg font-semibold text-gray-800 mb-1">List Mode Settings</h3>
          <p className="text-gray-600 mb-6">Optimise the results page for quick, focused result lookups</p>

          <div className="mb-8">
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Enable List Mode
                  </label>
                  <p className="text-sm text-gray-500 mt-1">
                    Streamline the results page for faster result lookups with customizable options
                  </p>
                </div>
                <button
                  onClick={handleToggleListMode}
                  disabled={savingListMode}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 ${
                    listModeEnabled ? 'bg-gray-900' : 'bg-gray-400'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      listModeEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {listModeEnabled && (
                <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <label className="text-sm font-medium text-gray-700">
                        Exact Bib Number Search
                      </label>
                      <p className="text-sm text-gray-500 mt-1">
                        Search returns only exact bib number matches. Typing "10" will only find bib #10, not #100 or #1001
                      </p>
                    </div>
                    <button
                      onClick={() => handleToggleListModeSetting('exact_bib')}
                      disabled={savingListMode}
                      className={`ml-4 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 ${
                        listModeExactBibSearch ? 'bg-gray-900' : 'bg-gray-400'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          listModeExactBibSearch ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <label className="text-sm font-medium text-gray-700">
                        Hide Filters Button
                      </label>
                      <p className="text-sm text-gray-500 mt-1">
                        Remove the filters button to simplify the interface and reduce distractions
                      </p>
                    </div>
                    <button
                      onClick={() => handleToggleListModeSetting('hide_filters')}
                      disabled={savingListMode}
                      className={`ml-4 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 ${
                        listModeHideFilters ? 'bg-gray-900' : 'bg-gray-400'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          listModeHideFilters ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                                    <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <label className="text-sm font-medium text-gray-700">
                        Simplified Result View
                      </label>
                      <p className="text-sm text-gray-500 mt-1">
                        Show essential information in a clean, mobile-optimized view instead of the full result details
                      </p>
                    </div>
                    <button
                      onClick={() => handleToggleListModeSetting('disable_modal')}
                      disabled={savingListMode}
                      className={`ml-4 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 ${
                        listModeDisableResultModal ? 'bg-gray-900' : 'bg-gray-400'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          listModeDisableResultModal ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <label className="text-sm font-medium text-gray-700">
                        Auto-Select Search Input
                      </label>
                      <p className="text-sm text-gray-500 mt-1">
                        Automatically highlight the search box text when focused, allowing QR scanners to instantly overwrite previous searches
                      </p>
                    </div>
                    <button
                      onClick={() => handleToggleListModeSetting('auto_select_search')}
                      disabled={savingListMode}
                      className={`ml-4 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 ${
                        listModeAutoSelectSearch ? 'bg-gray-900' : 'bg-gray-400'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          listModeAutoSelectSearch ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              )}
              
            </div>
          </div>
        </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-1">Visual Customization</h3>
              <p className="text-gray-600 mb-6">Customize the appearance of your shared results page</p>
              
              {/* Download Settings */}
              <div className="mb-8">
                <h4 className="text-base font-medium text-gray-800 mb-4 flex items-center gap-2">
                  <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                  Download Settings
                </h4>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Allow Results Download
                      </label>
                      <p className="text-sm text-gray-500 mt-1">
                        When disabled, visitors won't see the download button on the results page
                      </p>
                    </div>
                    <button
                      onClick={handleToggleDownload}
                      disabled={savingDownloadSetting}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 ${
                        disableDownload ? 'bg-gray-400' : 'bg-gray-900'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          disableDownload ? 'translate-x-1' : 'translate-x-6'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              <div className="space-y-8">
                {/* Event Logo Subsection */}
                <div>
                  <h4 className="text-base font-medium text-gray-800 mb-4 flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    Event Logo
                  </h4>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    {event.shared_results_logo?.url ? (
                      <div className="space-y-4">
                        <div className="flex items-center gap-4">
                          <div className="h-16 w-32 bg-white rounded-md overflow-hidden border border-gray-200">
                            <img
                              src={event.shared_results_logo.url}
                              alt="Event Logo"
                              className="h-full w-full object-contain"
                            />
                          </div>
                          <button
                            onClick={handleRemoveEventLogo}
                            disabled={uploadingLogo}
                            className="text-red-600 hover:text-red-700 disabled:opacity-50 p-2 rounded-full hover:bg-red-50 transition-colors"
                            title="Remove logo"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Logo Height
                          </label>
                          <div className="flex items-center gap-4">
                            <Eye className="w-4 h-4 text-gray-400" />
                            <div className="flex-1">
                              <input
                                type="range"
                                min="20"
                                max="80"
                                value={event.shared_results_logo.height || 40}
                                onChange={(e) => handleUpdateEventLogoHeight(parseInt(e.target.value))}
                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                              />
                            </div>
                            <div className="w-16 text-sm text-gray-600 tabular-nums text-right">
                              {event.shared_results_logo.height || 40}px
                            </div>
                          </div>
                          <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>Small</span>
                            <span>Large</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleEventLogoUpload}
                          disabled={uploadingLogo}
                          className="w-full px-3 py-2 text-sm text-gray-500
                            file:mr-4 file:py-2 file:px-4
                            file:rounded-md file:border-0
                            file:text-sm file:font-semibold
                            file:bg-blue-50 file:text-blue-700
                            hover:file:bg-blue-100 disabled:opacity-50"
                        />
                        <p className="mt-2 text-sm text-gray-600">
                          Upload your event logo. Maximum file size: 2MB. Supported formats: PNG, JPG, GIF
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Timing Company Logo Subsection */}
                <div>
                  <h4 className="text-base font-medium text-gray-800 mb-4 flex items-center gap-2">
                    <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                    Timing Company Logo
                  </h4>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    {event.timing_company_logo?.url ? (
                      <div className="flex items-center gap-4">
                        <div className="h-16 w-32 bg-white rounded-md overflow-hidden border border-gray-200">
                          <img
                            src={event.timing_company_logo.url}
                            alt="Timing Company Logo"
                            className="h-full w-full object-contain"
                          />
                        </div>
                        <button
                          onClick={handleRemoveTimingLogo}
                          disabled={uploadingTimingLogo}
                          className="text-red-600 hover:text-red-700 disabled:opacity-50 p-2 rounded-full hover:bg-red-50 transition-colors"
                          title="Remove timing logo"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    ) : (
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleTimingLogoUpload}
                          disabled={uploadingTimingLogo}
                          className="w-full px-3 py-2 text-sm text-gray-500
                            file:mr-4 file:py-2 file:px-4
                            file:rounded-md file:border-0
                            file:text-sm file:font-semibold
                            file:bg-purple-50 file:text-purple-700
                            hover:file:bg-purple-100 disabled:opacity-50"
                        />
                        <p className="mt-2 text-sm text-gray-600">
                          Upload your timing company logo. Maximum file size: 2MB. Supported formats: PNG, JPG, GIF
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Background Image Subsection */}
                <div>
                  <h4 className="text-base font-medium text-gray-800 mb-4 flex items-center gap-2">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                    Background Image
                  </h4>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    {event.shared_results_background?.url ? (
                      <div className="space-y-4">
                        <div className="flex items-start gap-4">
                          <div className="h-32 flex-1 bg-white rounded-md overflow-hidden border border-gray-200">
                            <img
                              src={event.shared_results_background.url}
                              alt="Background Preview"
                              className="h-full w-full object-cover"
                            />
                          </div>
                          <button
                            onClick={handleRemoveBackground}
                            disabled={uploadingBackground}
                            className="text-red-600 hover:text-red-700 disabled:opacity-50 p-2 rounded-full hover:bg-red-50 transition-colors"
                            title="Remove background"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Background Opacity
                          </label>
                          <div className="flex items-center gap-4">
                            <Eye className="w-4 h-4 text-gray-400" />
                            <div className="flex-1">
                              <input
                                type="range"
                                min="0"
                                max="100"
                                value={Math.round((event.shared_results_background.opacity || 0) * 100)}
                                onChange={(e) => handleUpdateBackgroundOpacity(parseInt(e.target.value) / 100)}
                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                              />
                            </div>
                            <div className="w-16 text-sm text-gray-600 tabular-nums text-right">
                              {Math.round((event.shared_results_background.opacity || 0) * 100)}%
                            </div>
                          </div>
                          <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>Transparent</span>
                            <span>Solid</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleBackgroundUpload}
                          disabled={uploadingBackground}
                          className="w-full px-3 py-2 text-sm text-gray-500
                            file:mr-4 file:py-2 file:px-4
                            file:rounded-md file:border-0
                            file:text-sm file:font-semibold
                            file:bg-emerald-50 file:text-emerald-700
                            hover:file:bg-emerald-100 disabled:opacity-50"
                        />
                        <p className="mt-2 text-sm text-gray-600">
                          Upload a background image for your results page. Maximum file size: 5MB. Supported formats: PNG, JPG, GIF
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* Preview Section */}
          </div>
        </div>
      </div>
      
      {/* Custom Remove Confirmation Dialog */}
      {showRemoveConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60] backdrop-blur-sm">
          <div className="bg-white/95 backdrop-blur-md rounded-xl shadow-xl w-full max-w-md p-6 border border-white/50">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Remove Custom URL</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to remove this custom URL? This will make your current custom link inaccessible.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowRemoveConfirm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowRemoveConfirm(false);
                  setError(null);
                  setSavingSlug(true);
                  
                  supabase
                    .from('events')
                    .update({
                      custom_url_slug: null
                    })
                    .eq('id', event.id)
                    .then(({ error: updateError }) => {
                      if (updateError) {
                        setError(updateError.message);
                      } else {
                        setCustomSlug('');
                        onUpdate();
                      }
                    })
                    .finally(() => {
                      setSavingSlug(false);
                    });
                }}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
              >
                Remove URL
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}